import { useEffect, useState } from "react";
import { Loader2, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ConvertToCustomerButton } from "@/components/ConvertToCustomerButton";

const STATUSES = ["new", "contacted", "quoted", "won"];
const TONES = {
  new: "bg-brand-ice/30 text-blue-900",
  contacted: "bg-amber-100 text-amber-800",
  quoted: "bg-purple-100 text-purple-800",
  won: "bg-green-100 text-green-800",
};

const PHOTO_LABELS = {
  outdoor_unit: "Outside unit",
  outdoor_nameplate: "Outside nameplate",
  indoor_unit: "Inside unit",
  indoor_nameplate: "Inside nameplate",
  install_area: "Installation area",
};

const PhotoThumb = ({ path, label }) => (
  <figure>
    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/estimate-photos/${path}`} alt={label}
      className="w-24 h-24 object-cover rounded-sm border border-zinc-200" />
    <figcaption className="text-xs text-zinc-500 mt-1">{label}</figcaption>
  </figure>
);

const EstimatePhotos = ({ estimate }) => {
  const legacy = [
    estimate.unit_photo && { key: "legacy-unit", path: estimate.unit_photo.storage_path, label: "Existing unit" },
    estimate.nameplate_photo && { key: "legacy-nameplate", path: estimate.nameplate_photo.storage_path, label: "Nameplate" },
  ].filter(Boolean);
  const modern = Object.entries(estimate.photos || {}).map(([kind, meta]) => ({
    key: kind, path: meta.storage_path, label: PHOTO_LABELS[kind] || kind,
  }));
  const all = [...legacy, ...modern];
  if (all.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-3 mt-2">
      {all.map((p) => <PhotoThumb key={p.key} path={p.path} label={p.label} />)}
    </div>
  );
};

const EstimateRow = ({ estimate: e, saving, onStatus, onConvert }) => (
  <li data-testid={`admin-estimate-${e.id}`} className="py-4 first:pt-0 last:pb-0">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <p className="font-medium text-brand-ink">{e.name} <span className="text-zinc-500 font-normal">· {e.phone}</span></p>
      <div className="flex items-center gap-2">
        <span className="text-xs text-zinc-500">{e.created_at?.slice(0, 10)}</span>
        <span data-testid={`admin-estimate-status-badge-${e.id}`}
          className={`text-xs font-bold px-2 py-0.5 rounded-sm uppercase tracking-wide ${TONES[e.status] || TONES.new}`}>
          {e.status}
        </span>
        {saving === e.id ? (
          <Loader2 className="w-4 h-4 text-brand animate-spin" />
        ) : (
          <Select value={e.status} onValueChange={(v) => onStatus(e.id, v)}>
            <SelectTrigger data-testid={`admin-estimate-status-select-${e.id}`} className="rounded-sm h-8 w-32 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>
    </div>
    <p className="text-sm text-zinc-600 mt-0.5">
      {e.square_footage} sq ft · {e.floors} floor(s){e.city ? ` · ${e.city}` : ""}{e.preferred_brand ? ` · Brand: ${e.preferred_brand}` : ""} · {e.email}
    </p>
    {e.message && <p className="text-sm text-zinc-500 mt-0.5">{e.message}</p>}
    <ConvertToCustomerButton name={e.name} email={e.email} phone={e.phone} testId={`admin-estimate-convert-customer-${e.id}`} />
    {e.status === "won" && !e.converted_record_id && (
      <button onClick={() => onConvert(e.id)} disabled={saving === e.id} data-testid={`admin-estimate-convert-btn-${e.id}`}
        className="mt-2 bg-brand hover:bg-brand-dark disabled:opacity-60 text-white font-bold px-4 py-2 rounded-sm text-xs flex items-center gap-1.5 transition-colors duration-200">
        <ClipboardCheck className="w-3.5 h-3.5" /> Convert to Service Record
      </button>
    )}
    {e.converted_record_id && (
      <p data-testid={`admin-estimate-converted-${e.id}`} className="mt-2 text-xs font-bold text-green-700 flex items-center gap-1.5">
        <ClipboardCheck className="w-3.5 h-3.5" /> Converted to service record
      </p>
    )}
    <EstimatePhotos estimate={e} />
  </li>
);

const EstimatesList = ({ estimates, saving, onStatus, onConvert }) => {
  if (estimates === null) return <Loader2 className="w-6 h-6 text-brand animate-spin" />;
  if (estimates.length === 0) return <p data-testid="admin-estimates-empty" className="text-sm text-zinc-600">No estimate requests yet.</p>;
  return (
    <ul className="divide-y divide-zinc-100">
      {estimates.map((e) => (
        <EstimateRow key={e.id} estimate={e} saving={saving} onStatus={onStatus} onConvert={onConvert} />
      ))}
    </ul>
  );
};

export const AdminEstimates = () => {
  const [estimates, setEstimates] = useState(null);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    api.get("/admin/estimates").then((r) => setEstimates(r.data.estimates)).catch(() => setEstimates([]));
  }, []);

  const updateStatus = async (id, status) => {
    setSaving(id);
    try {
      await api.patch(`/admin/estimates/${id}`, { status });
      setEstimates((prev) => prev.map((e) => (e.id === id ? { ...e, status } : e)));
      toast.success(`Estimate marked ${status}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(null);
    }
  };

  const convert = async (id) => {
    setSaving(id);
    try {
      const r = await api.post(`/admin/estimates/${id}/convert`);
      setEstimates((prev) => prev.map((e) => (e.id === id ? { ...e, status: "won", converted_record_id: r.data.record.id } : e)));
      toast.success("Service record created from estimate");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(null);
    }
  };

  return (
    <div data-testid="admin-estimates-card" className="bg-white border border-zinc-200 p-6 rounded-sm mt-6">
      <h2 className="font-heading font-bold text-lg text-brand-ink mb-4">AC Replacement Estimates (Admin)</h2>
      <EstimatesList estimates={estimates} saving={saving} onStatus={updateStatus} onConvert={convert} />
    </div>
  );
};
